import * as React from 'react';

import { Icon } from '../icon/icon.tsx';

import { useClickAway } from '../hooks.tsx';

import './search-bar.css';

interface SearchBarProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  showHelpIcon?: boolean;
  required?: boolean;
  state?: 'default' | 'error' | 'warning';
  stateText?: string;
  stateDefaultIcon?: React.ReactNode;
  datalist: {
    defaultIcon?: React.ReactNode;
    iconPosition?: 'leading' | 'trailing';
    options: Array<{
      value: string;
      icon?: React.ReactNode;
    }>;
  };
}

const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      type = 'text',
      disabled,
      onChange,
      showHelpIcon = false,
      state = 'default',
      datalist,
      required,
      stateText,
      stateDefaultIcon,
      label,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => ref.current as HTMLInputElement,
    );
    const [iconColor, setIconColor] = React.useState(
      'var(--color_neutral-dark)',
    );
    const [clicked, setClicked] = React.useState(false);
    const [clickedHelpIcon, setClickedHelpIcon] = React.useState(false);
    const [showNotFound, setShowNotFound] = React.useState(false);
    const [showClearIcon, setShowClearIcon] = React.useState(false);
    const datalistRef = React.useRef<HTMLDataListElement | null>(null);
    const mainWrapperRef = useClickAway(() => {
      if (datalistRef.current) {
        if (datalistRef.current.style) {
          datalistRef.current.style.display = 'none';
        }
      }
      setShowNotFound(false);
      if (ref.current) {
        if (ref.current.value === '') {
          setClicked(false);
        }
      }
    });
    const iconColorRegistry = {
      default: 'var(--color_neutral-dark)',
      error: 'var(--color_error-dark)',
      warning: 'var(--color_warning)',
      disabled: 'var(--color_disabled)',
    };

    return (
      <div
        className={
          disabled
            ? `search-bar-wrapper search-bar-${state} search-bar-disabled`
            : `search-bar-wrapper search-bar-${state}`
        }
      >
        {showHelpIcon && (
          <div className="sb-top-wrapper">
            <button
              type="button"
              className="help-icon-bg"
              onClick={() => {
                setClickedHelpIcon(!clickedHelpIcon);
              }}
              style={{
                backgroundColor: clickedHelpIcon
                  ? 'var(--color_primary-100)'
                  : '',
              }}
            >
              <Icon
                type={clickedHelpIcon ? 'question-fill' : 'question'}
                size="medium"
                color="var(--color_primary-darker)"
                style={{
                  cursor: 'pointer',
                }}
              />
            </button>
          </div>
        )}
        <div className="sb-main-wrapper" ref={mainWrapperRef}>
          <div>
            <div
              className={`label-${state} ${
                clicked && disabled !== true ? `clicked-${state}` : ''
              }`}
            >
              <span
                className={`${
                  clicked ? 'label-leading-icon_clicked' : 'label-leading-icon'
                }`}
              >
                <Icon type="search" size="medium" />
              </span>{' '}
              {label}{' '}
              {required && (
                <span style={{ color: 'var(--color_error)' }}>*</span>
              )}
            </div>
            <span className="leading-icon">
              <Icon type="search" size="medium" color={iconColor} />
            </span>
            <input
              className="with-leading-icon"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onChange?.(e);
                if (e.target.value !== '') {
                  setClicked(true);
                  setShowClearIcon(true);
                } else {
                  setClicked(false);
                  setShowClearIcon(false);
                }

                if (e.target.value === '' && clicked) {
                  if (datalistRef.current) {
                    if (datalistRef.current.style) {
                      datalistRef.current.style.display = 'block';
                    }
                  }
                }

                if (ref.current) {
                  if (datalistRef.current) {
                    if (datalistRef.current.style) {
                      datalistRef.current.style.display = 'block';
                    }
                  }
                }

                if (datalistRef.current) {
                  let notFound = 0;
                  for (const wrapper of datalistRef.current.children) {
                    if (ref.current) {
                      if (
                        (
                          wrapper.children[
                            datalist.iconPosition === 'leading' ? 1 : 0
                          ] as HTMLButtonElement
                        ).value
                          .toUpperCase()
                          .indexOf(ref.current.value.toUpperCase()) > -1
                      ) {
                        (wrapper as HTMLElement).style.display = 'flex';
                      } else {
                        (wrapper as HTMLElement).style.display = 'none';
                        notFound++;
                      }
                    }
                  }
                  if (notFound >= datalist.options.length) {
                    setShowNotFound(true);
                    if (datalistRef.current) {
                      if (datalistRef.current.style) {
                        datalistRef.current.style.display = 'none';
                      }
                    }
                  } else {
                    setShowNotFound(false);
                  }
                }
              }}
              onFocus={() => {
                setIconColor(
                  state === 'default'
                    ? 'var(--color_primary)'
                    : state === 'error'
                    ? 'var(--color_error-light)'
                    : 'var(--color_warning-light)',
                );

                if (datalistRef.current) {
                  if (datalistRef.current.style) {
                    if (!showNotFound) {
                      datalistRef.current.style.display = 'block';
                    }
                  }
                }

                if (ref.current) {
                  if (datalistRef.current) {
                    if (datalistRef.current.style) {
                      datalistRef.current.style.display = 'block';
                    }
                  }
                }

                if (ref.current) {
                  if (ref.current.value === '' && clicked) {
                    if (datalistRef.current) {
                      if (datalistRef.current.style) {
                        datalistRef.current.style.display = 'block';
                      }
                    }
                  }
                }

                if (datalistRef.current) {
                  let notFound = 0;
                  for (const wrapper of datalistRef.current.children) {
                    if (ref.current) {
                      if (
                        (
                          wrapper.children[
                            datalist.iconPosition === 'leading' ? 1 : 0
                          ] as HTMLButtonElement
                        ).value
                          .toUpperCase()
                          .indexOf(ref.current.value.toUpperCase()) > -1
                      ) {
                        (wrapper as HTMLElement).style.display = 'flex';
                      } else {
                        (wrapper as HTMLElement).style.display = 'none';
                        notFound++;
                      }
                    }
                  }
                  if (notFound >= datalist.options.length) {
                    setShowNotFound(true);
                    if (datalistRef.current) {
                      if (datalistRef.current.style) {
                        datalistRef.current.style.display = 'none';
                      }
                    }
                  } else {
                    setShowNotFound(false);
                  }
                }
              }}
              type={type}
              disabled={disabled}
              ref={ref}
              list=""
              autoComplete="off"
              {...props}
            />
            {showClearIcon && (
              <button
                type="button"
                className="fix-icon-erase"
                onClick={() => {
                  if (ref.current) {
                    ref.current.value = '';
                    ref.current.focus();
                  }

                  setShowClearIcon(false);
                  setShowNotFound(false);

                  if (datalistRef.current) {
                    if (datalistRef.current.style) {
                      datalistRef.current.style.display = 'block';
                    }
                  }
                }}
              >
                <Icon type="x" size="small" />
              </button>
            )}
            {stateText && (
              <div className="sb-bottom-wrapper">
                <div className="left">
                  {state === 'default' && stateDefaultIcon ? (
                    stateDefaultIcon
                  ) : (
                    <Icon
                      type={
                        state === 'error'
                          ? 'x'
                          : state === 'warning'
                          ? 'warning'
                          : null
                      }
                      color={
                        disabled === true
                          ? iconColorRegistry.disabled
                          : iconColorRegistry[state]
                      }
                    />
                  )}
                  {state && <div className="state-text">{stateText}</div>}
                </div>
              </div>
            )}
            {showNotFound && (
              <div className="not-found">Not results found.</div>
            )}
            <datalist ref={datalistRef} id={`${props.id}-list`} role="listbox">
              {datalist.options.map(option => {
                return (
                  <button
                    type="button"
                    className="option-wrapper"
                    style={{
                      justifyContent:
                        datalist.iconPosition === 'leading'
                          ? 'start'
                          : 'space-between',
                    }}
                    onClick={() => {
                      setClicked(true);
                      setShowClearIcon(true);
                      if (datalistRef.current) {
                        if (datalistRef.current.style) {
                          datalistRef.current.style.display = 'none';
                        }
                      }

                      if (ref.current) {
                        ref.current.value = option.value;
                      }
                    }}
                  >
                    {datalist.iconPosition === 'leading' && (
                      <div className="icon-leading">
                        {option.icon ? option.icon : datalist.defaultIcon}
                      </div>
                    )}
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                    {datalist.iconPosition === 'trailing' && (
                      <div className="icon-trailing">
                        {option.icon ? option.icon : datalist.defaultIcon}
                      </div>
                    )}
                  </button>
                );
              })}
            </datalist>
          </div>
        </div>
      </div>
    );
  },
);

export { SearchBar };
